import { Image, Empty, Button, Modal } from "antd";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import EditPurchaseDetailsForm from "../../forms/EditPurchaseDetailsForm";
import EditSampleDetailsForm from "../../forms/EditSampleDetailsForm";
import moment from "moment";
import { buildDefaultValues, variantFieldMap } from "./helpers";
import { editSampleDetailsSchema } from "../../schemas/editSampleDetailsSchema";
import { editPurchaseDetailsSchema } from "../../schemas/editPurchaseDetailsSchema";

const SampleDetails = ({
  sampleDetails,
  orderDetails,
  onSubmit,
  isEditingPurchaseDetails,
  setIsEditingPurchaseDetails,
  isEditingSampleDetails,
  setIsEditingSampleDetails,
  inProgressEditSampleDetails,
  userRole,
}) => {
  const isSampleDetailsEmpty =
    !sampleDetails ||
    Object.keys(sampleDetails).length === 0 ||
    Object.values(sampleDetails).every((value) => !value);
  // Get the current variant fields for view
  const variantFields = variantFieldMap[orderDetails?.sampleVariant] || [];

  // For purchase details
  const purchaseFields =
    variantFieldMap[orderDetails?.sampleVariant]?.filter((f) =>
      [
        "nameOfTheShop",
        "addressOfTheShop",
        "dateOfPurchase",
        "quantityOfSample",
        "costOfSample",
        "typeOfShop",
      ].includes(f)
    ) || [];

  const purchaseDefaultValues = buildDefaultValues(
    purchaseFields,
    sampleDetails
  );

  // For sample details
  const sampleFields =
    variantFieldMap[orderDetails?.sampleVariant]?.filter((f) =>
      [
        "manufacturingDatePresent",
        "manufacturingDate",
        "expiryDatePresent",
        "expiryDate",
        "brandNamePresent",
        "brandName",
        "manufacturerNamePresent",
        "manufacturersName",
        "lotNumberPresent",
        "lotNumber",
        "packagingType",
        "samplePackedIn",
        "fssaiLogoPresent",
        "vegSymbolPresent",
        "jaivikBharatSymbolPresent",
        "qrCodeId",
        "sampleSealNo",
        "manufacturerPlantAddressPresent",
        "manufacturingPlantAddress",
        "fssaiLicenseNumberPresent",
        "fssaiLicenseNumber",
        "countryOfOriginPresent",
        "countryOfOrigin",
        "sampleClaim",
        "sampleClaimDescription",
      ].includes(f)
    ) || [];

  const sampleDefaultValues = buildDefaultValues(sampleFields, sampleDetails);

  const {
    control: editPurchaseDetailsControl,
    handleSubmit: editPurchaseDetailsHandleSubmit,
    formState: { errors: editPurchaseDetailsErrors },
    reset: resetEditPurchaseDetailsForm,
    setValue: editPurchaseDetailsSetValue,
    watch: editPurchaseDetailsWatch,
  } = useForm({
    resolver: yupResolver(
      editPurchaseDetailsSchema(purchaseFields, orderDetails)
    ),
    defaultValues: purchaseDefaultValues,
  });

  const {
    control: editSampleDetailsControl,
    handleSubmit: editSampleDetailsHandleSubmit,
    formState: { errors: editSampleDetailsErrors },
    reset: resetEditSampleDetailsForm,
    setValue: editSampleDetailsSetValue,
    watch: editSampleDetailsWatch,
  } = useForm({
    resolver: yupResolver(
      editSampleDetailsSchema(sampleFields, orderDetails?.sampleVariant)
    ),
    defaultValues: sampleDefaultValues,
  });

  useEffect(() => {
    if (sampleDetails) {
      resetEditPurchaseDetailsForm(purchaseDefaultValues);
      resetEditSampleDetailsForm(sampleDefaultValues);
    }
  }, [
    sampleDetails,
    orderDetails,
    resetEditPurchaseDetailsForm,
    resetEditSampleDetailsForm,
  ]);

  if (isSampleDetailsEmpty) {
    return (
      <div
        style={{
          padding: "24px",
          background: "#fff",
          border: "1px solid #E0E0E0",
          borderRadius: 8,
        }}
      >
        <Empty
          description="No sample has been collected for this order yet. Sample details will appear here once the collection is completed"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  if (!sampleDetails) return <div>No sample details</div>;

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div
          style={{
            padding: "20px 30px",
            border: "1px solid #E0E0E0",
            borderRadius: 8,
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid #D9D9D9",
              alignItems: "center",
              paddingBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: 16,
                  marginBottom: 0,
                }}
              >
                Purchase Details
              </p>
            </div>
            {userRole === "company_admin" ||userRole === "support_team"||
            orderDetails?.status?.value === "reportUploaded" ||
            orderDetails?.status?.value === "completed" ? (
              <></>
            ) : (
              <Button
                type="primary"
                onClick={() =>
                  setIsEditingPurchaseDetails(!isEditingPurchaseDetails)
                }
              >
                Edit
              </Button>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {variantFields.includes("nameOfTheShop") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Name of Shop :</span>{" "}
                {sampleDetails.nameOfTheShop}
              </p>
            )}
            {variantFields.includes("addressOfTheShop") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Address of Shop :</span>{" "}
                {sampleDetails.addressOfTheShop}
              </p>
            )}
            {variantFields.includes("dateOfPurchase") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Date of Purchase :</span>{" "}
                {sampleDetails.dateOfPurchase
                  ? moment(sampleDetails.dateOfPurchase, [
                      "DD-MM-YYYY",
                      moment.ISO_8601,
                      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
                    ]).format("DD-MM-YYYY")
                  : ""}
              </p>
            )}
            {variantFields.includes("quantityOfSample") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Sample Weight (grams) :
                </span>{" "}
                {sampleDetails.quantityOfSample}
              </p>
            )}
            {userRole !== "company_admin" &&
              variantFields.includes("costOfSample") && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>Sample Cost in ₹:</span>{" "}
                  {sampleDetails.costOfSample}
                </p>
              )}
            {variantFields.includes("typeOfShop") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Type of Shop :</span>{" "}
                {sampleDetails.typeOfShop}
              </p>
            )}
            {userRole !== "company_admin" &&
              variantFields.includes("photographOfInvoice") && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Photograph of Invoice :
                  </span>{" "}
                  <Image
                    src={sampleDetails.photographOfInvoice}
                    alt="Invoice"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("photographOfShop") &&
              Array.isArray(sampleDetails.photographOfShop) &&
              sampleDetails.photographOfShop.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold", width: "100%" }}>
                    Photograph of Shop :
                  </span>
                  {sampleDetails.photographOfShop.map((photo, index) => (
                    <Image
                      key={index}
                      src={photo}
                      alt={`Shop ${index + 1}`}
                      width={200}
                      height={200}
                      style={{
                        cursor: "pointer",
                        objectFit: "contain",
                      }}
                      preview={{
                        mask: "Click to view",
                      }}
                    />
                  ))}
                </div>
              )}
          </div>
        </div>
        <div
          style={{
            padding: "20px 30px",
            border: "1px solid #E0E0E0",
            borderRadius: 8,
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid #D9D9D9",
              alignItems: "center",
              paddingBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: 16,
                  marginBottom: 0,
                }}
              >
                Sample Details
              </p>
            </div>
            {userRole === "company_admin" || userRole === "support_team" || 
            orderDetails?.status?.value === "reportUploaded" ||
            orderDetails?.status?.value === "completed" ? (
              <></>
            ) : (
              <Button
                type="primary"
                onClick={() =>
                  setIsEditingSampleDetails(!isEditingSampleDetails)
                }
              >
                Edit
              </Button>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {variantFields.includes("manufacturingDatePresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Manufacturing Date Present? :
                </span>{" "}
                {sampleDetails.manufacturingDatePresent}
              </p>
            )}
            {variantFields.includes("manufacturingDate") &&
              sampleDetails.manufacturingDatePresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Date of Manufacturing :
                  </span>{" "}
                  {sampleDetails.manufacturingDate
                    ? moment(sampleDetails.manufacturingDate, [
                        "DD-MM-YYYY",
                        moment.ISO_8601,
                        "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
                      ]).format("DD-MM-YYYY")
                    : ""}
                </p>
              )}
            {variantFields.includes("expiryDatePresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Expiry Date Present? :
                </span>{" "}
                {sampleDetails.expiryDatePresent}
              </p>
            )}
            {variantFields.includes("expiryDate") &&
              sampleDetails.expiryDatePresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>Expiry Date :</span>{" "}
                  {sampleDetails.expiryDate
                    ? moment(sampleDetails.expiryDate, [
                        "DD-MM-YYYY",
                        moment.ISO_8601,
                        "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
                      ]).format("DD-MM-YYYY")
                    : ""}
                </p>
              )}
            {variantFields.includes("expiryDatePhoto") &&
              sampleDetails.expiryDatePresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Expiry Date Photo :
                  </span>{" "}
                  <Image
                    src={sampleDetails.validityPhoto}
                    alt="Sample"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("brandNamePresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Brand Name Present? :
                </span>{" "}
                {sampleDetails.brandNamePresent}
              </p>
            )}
            {variantFields.includes("brandName") &&
              sampleDetails.brandNamePresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>Brand Name :</span>{" "}
                  {sampleDetails.brandName}
                </p>
              )}
            {variantFields.includes("manufacturerNamePresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Manufacture Name Present? :
                </span>{" "}
                {sampleDetails.manufacturerNamePresent}
              </p>
            )}
            {variantFields.includes("manufacturersName") &&
              sampleDetails.manufacturerNamePresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Manufacturers Name :
                  </span>{" "}
                  {sampleDetails.manufacturersName}
                </p>
              )}
            {variantFields.includes("manufacturerNamePhoto") &&
              sampleDetails.manufacturerNamePresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Manufacturers Name Photo :
                  </span>{" "}
                  <Image
                    src={sampleDetails.manufacturerNamePhoto}
                    alt="Manufacturers Name"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("lotNumberPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Lot Number Present? :
                </span>{" "}
                {sampleDetails.lotNumberPresent}
              </p>
            )}
            {variantFields.includes("lotNumber") &&
              sampleDetails.lotNumberPresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Lot / Batch Number :
                  </span>{" "}
                  {sampleDetails.lotNumber}
                </p>
              )}
            {variantFields.includes("generalPackingPhoto") &&
              Array.isArray(sampleDetails.generalPackingPhoto) &&
              sampleDetails.generalPackingPhoto.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold", width: "100%" }}>
                    General Packing Photo :
                  </span>
                  {sampleDetails.generalPackingPhoto.map((photo, index) => (
                    <Image
                      key={index}
                      src={photo}
                      alt={`General Packing ${index + 1}`}
                      width={200}
                      height={200}
                      style={{
                        cursor: "pointer",
                        objectFit: "contain",
                      }}
                      preview={{
                        mask: "Click to view",
                      }}
                    />
                  ))}
                </div>
              )}

            {variantFields.includes("packagingType") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Packaging Type :</span>{" "}
                {sampleDetails.packagingType}
              </p>
            )}
            {variantFields.includes("packagingTypePhoto") &&
              sampleDetails.packagingTypePhoto && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Packaging Image :</span>{" "}
                  <Image
                    src={sampleDetails.packagingTypePhoto}
                    alt="Sample Seal"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}

            {variantFields.includes("samplePackedIn") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Sample Packed in :</span>{" "}
                {sampleDetails.samplePackedIn}
              </p>
            )}
            {variantFields.includes("fssaiLogoPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Does the Sample have FSSAI logo ? :
                </span>{" "}
                {sampleDetails.fssaiLogoPresent}
              </p>
            )}
            {variantFields.includes("fssaiLogoPhoto") &&
              sampleDetails.fssaiLogoPresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>FSSAI logo Photo :</span>{" "}
                  <Image
                    src={sampleDetails.fssaiLogoPhoto}
                    alt="Sample"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("vegSymbolPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Does the sample have Veg/Non-veg symbol?
                </span>{" "}
                {sampleDetails.vegSymbolPresent}
              </p>
            )}
            {variantFields.includes("vegSymbolPhoto") &&
              sampleDetails.vegSymbolPresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Veg/Non-veg Symbol Photo :
                  </span>{" "}
                  <Image
                    src={sampleDetails.vegSymbolPhoto}
                    alt="Sample"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("jaivikBharatSymbolPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Does the sample have Jaivik Bharat symbol ?
                </span>{" "}
                {sampleDetails.jaivikBharatSymbolPresent}
              </p>
            )}
            {variantFields.includes("jaivikBharatSymbolPhoto") &&
              sampleDetails.jaivikBharatSymbolPresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Jaivik Bharat Symbol Photo :
                  </span>{" "}
                  <Image
                    src={sampleDetails.jaivikBharatSymbolPhoto}
                    alt="jaivikBharatSymbolPhoto"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("qrCodeId") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>QR Code Id :</span>{" "}
                {sampleDetails.qrCodeId}
              </p>
            )}
            {variantFields.includes("qrCodePhoto") && (
              <p
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>
                  Sample Photo with QR Code :
                </span>{" "}
                <Image
                  src={sampleDetails.qrCodePhoto}
                  alt="QR Code"
                  width={200}
                  height={200}
                  style={{
                    cursor: "pointer",
                    objectFit: "contain",
                  }}
                  preview={{
                    mask: "Click to view",
                  }}
                />
              </p>
            )}
            {variantFields.includes("sampleSealNo") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>Sample Seal Number :</span>{" "}
                {sampleDetails.sampleSealNo}
              </p>
            )}
            {variantFields.includes("sampleSealPhoto") && (
              <p
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Sample Seal Photo :</span>{" "}
                <Image
                  src={sampleDetails.sampleSealPhoto}
                  alt="Sample Seal"
                  width={200}
                  height={200}
                  style={{
                    cursor: "pointer",
                    objectFit: "contain",
                  }}
                  preview={{
                    mask: "Click to view",
                  }}
                />
              </p>
            )}
            {variantFields.includes("samplerSelfie") && (
              <p
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Sampler Selfie :</span>{" "}
                <Image
                  src={sampleDetails.samplerSelfie}
                  alt="Sampler Selfie"
                  width={200}
                  height={200}
                  style={{
                    cursor: "pointer",
                    objectFit: "contain",
                  }}
                  preview={{
                    mask: "Click to view",
                  }}
                />
              </p>
            )}
            {variantFields.includes("manufacturerPlantAddressPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Manufacturing Plant Address Present? :
                </span>{" "}
                {sampleDetails.manufacturerPlantAddressPresent}
              </p>
            )}
            {variantFields.includes("manufacturingPlantAddress") &&
              sampleDetails.manufacturerPlantAddressPresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Manufacturing Plant Address :
                  </span>{" "}
                  {sampleDetails.manufacturingPlantAddress}
                </p>
              )}
            {variantFields.includes("fssaiLicenseNumberPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  FSSAI License Number Present? :
                </span>{" "}
                {sampleDetails.fssaiLicenseNumberPresent}
              </p>
            )}
            {variantFields.includes("fssaiLicenseNumber") &&
              sampleDetails.fssaiLicenseNumberPresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    FSSAI License Number :
                  </span>{" "}
                  {sampleDetails.fssaiLicenseNumber}
                </p>
              )}
            {variantFields.includes("fssaiLicenseNumberPhoto") &&
              sampleDetails.fssaiLicenseNumberPresent === "Yes" && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Photograph of FSSAI License Number :
                  </span>{" "}
                  <Image
                    src={sampleDetails.fssaiLicenseNumberPhoto}
                    alt="FSSAI License Number"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
            {variantFields.includes("countryOfOriginPresent") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Country of origin present? :
                </span>{" "}
                {sampleDetails.countryOfOriginPresent}
              </p>
            )}
            {variantFields.includes("countryOfOrigin") &&
              sampleDetails.countryOfOriginPresent === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Country of origin :
                  </span>{" "}
                  {sampleDetails.countryOfOrigin}
                </p>
              )}
            {variantFields.includes("sampleClaim") && (
              <p style={{ display: "flex", gap: "5px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Any Claim made on Sample? :
                </span>{" "}
                {sampleDetails.sampleClaim}
              </p>
            )}
            {variantFields.includes("sampleClaimDescription") &&
              sampleDetails.sampleClaim === "Yes" && (
                <p style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "bold" }}>
                    Sample Claim Description :
                  </span>{" "}
                  {sampleDetails.sampleClaimDescription}
                </p>
              )}
            {variantFields.includes("sampleClaimPhoto") &&
              sampleDetails.sampleClaim === "Yes" &&
              sampleDetails.sampleClaimPhoto && (
                <p
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    Photograph of Sample Claim :
                  </span>{" "}
                  <Image
                    src={sampleDetails.sampleClaimPhoto}
                    alt="Sample Claim photo"
                    width={200}
                    height={200}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "Click to view",
                    }}
                  />
                </p>
              )}
          </div>
        </div>
      </div>
      {isEditingPurchaseDetails && (
        <Modal
          title="Edit Purchase Details"
          visible={isEditingPurchaseDetails}
          onCancel={() => {
            setIsEditingPurchaseDetails(false);
            resetEditPurchaseDetailsForm();
          }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setIsEditingPurchaseDetails(false);
                resetEditPurchaseDetailsForm();
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={editPurchaseDetailsHandleSubmit(onSubmit)}
              loading={inProgressEditSampleDetails}
            >
              Submit
            </Button>,
          ]}
        >
          <EditPurchaseDetailsForm
            control={editPurchaseDetailsControl}
            errors={editPurchaseDetailsErrors}
            setValue={editPurchaseDetailsSetValue}
            variantField={variantFields}
          />
        </Modal>
      )}
      {isEditingSampleDetails && (
        <Modal
          width={800}
          title="Edit Sample Details"
          visible={isEditingSampleDetails}
          onCancel={() => {
            setIsEditingSampleDetails(false);
            resetEditSampleDetailsForm();
          }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setIsEditingSampleDetails(false);
                resetEditSampleDetailsForm();
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={editSampleDetailsHandleSubmit(onSubmit)}
              loading={inProgressEditSampleDetails}
            >
              Submit
            </Button>,
          ]}
        >
          <EditSampleDetailsForm
            control={editSampleDetailsControl}
            errors={editSampleDetailsErrors}
            setValue={editSampleDetailsSetValue}
            variantField={variantFields}
            variant={orderDetails?.sampleVariant}
            editSampleDetailsWatch={editSampleDetailsWatch}
          />
        </Modal>
      )}
    </>
  );
};

export default SampleDetails;
